import { createContext, useEffect, useMemo, useRef, useState } from 'react';

import { IMPERFECT_THEME_DOMAIN, MISFITS_THEME_DOMAIN } from 'constants/theme';
import { getBrandDomain } from 'utils';

const GlobalContext = createContext(null);

function GlobalProvider({ children }) {
    const brandDomain = getBrandDomain();
    const urlParams = new URLSearchParams(window.location.search);
    const openCart = urlParams.get('cart') || false;
    const [cartOpen, setCartOpen] = useState((window.isNativeApp && openCart === 'true') ?? false);
    const [ebtPaymentFlowOpen, setEbtPaymentFlowOpen] = useState(false);
    const [autocompleteOpen, setAutocompleteOpen] = useState(false);
    const [searchResultsLength, setSearchResultsLength] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [productAdded, setProductAddedState] = useState(false);
    const [productAddedID, setProductAddedID] = useState(null);
    const [seeAllCarouselId, setSeeAllCarouselId] = useState(null);
    const [scrollToSeeAllProductId, setScrollToSeeAllProductId] = useState(null);
    const [addCounter, setAddCounter] = useState(0);
    const [theme, setTheme] = useState(brandDomain ?? MISFITS_THEME_DOMAIN);
    const [alerts, setAlerts] = useState([]);
    const [appliedFilters, setAppliedFilters] = useState(() => {
        const storedFilters = localStorage.getItem('APPLIED_FILTERS');
        return storedFilters ? JSON.parse(storedFilters) : ['In Stock'];
    });
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [currentAisleId, setCurrentAisleId] = useState(null);
    const [openWindowOnboardingModalOpen, setOpenWindowOnboardingModalOpen] = useState(false);
    const [closedWindowOnboardingModalOpen, setClosedWindowOnboardingModalOpen] = useState(false);
    const [gwpErrorToast, setGwpErrorToast] = useState(false);

    const lastPath = useRef(null);

    // Products modal state
    const [productsModal, setProductsModal] = useState({
        open: false,
        sourceRoute: null,
    });

    // Recurring item confirmation modal state
    const [recurringModal, setRecurringModal] = useState({
        open: false,
        itemName: '',
    });

    // Sold-out recurring item modal state
    const [soldOutRecurringModal, setSoldOutRecurringModal] = useState({
        open: false,
        itemName: '',
        externalSku: '',
        onConfirm: null,
    });

    // Recurring item toast state
    const [recurringToast, setRecurringToast] = useState({
        open: false,
        itemName: '',
        action: '', // 'added' or 'removed'
        timestamp: null, // Used as key to force re-animation
    });

    // Recurring limit warning modal state
    const [recurringLimitWarningModal, setRecurringLimitWarningModal] = useState(false);

    /**
     * @property {Object} data - Carousel data containing items to display
     * @property {string} [data.name] - Name of the carousel
     * @property {string} [data.description] - Description of the carousel
     * @property {Array<Object>} data.items - Array of product items (ProductData objects)
     * @property {number|null} [deepLinkProductID] - Product ID to scroll to when modal opens
     * @property {Object} [productCardMetaData] - Metadata object for ProductCardV2
     * @property {string} [productCardMetaData.carouselID] - Carousel ID for ProductCardV2 metadata
     * @property {string} [productCardMetaData.carouselName] - Carousel name for ProductCardV2 metadata
     * @property {string} [productCardMetaData.carouselType] - Carousel type for ProductCardV2 metadata
     * @property {boolean} [productCardMetaData.isBanner] - Whether this card is from a banner
     * @property {number|null} [productID] - Product ID set dynamically when navigating from product card in modal
     */
    const productsModalDataRef = useRef(null);

    const productAddedTimer = useRef();

    useEffect(() => {
        localStorage.setItem('APPLIED_FILTERS', JSON.stringify(appliedFilters));
    }, [appliedFilters]);

    const setProductAdded = () => {
        setProductAddedState(true);

        if (productAddedTimer.current) {
            clearTimeout(productAddedTimer.current);
        }

        productAddedTimer.current = setTimeout(() => {
            setProductAddedState(false);
        }, 3000);
    };

    const contextValue = useMemo(
        () => ({
            autocompleteOpen,
            cartOpen,
            filtersOpen,
            isImperfectTheme: theme === IMPERFECT_THEME_DOMAIN,
            isMisfitsTheme: theme === MISFITS_THEME_DOMAIN,
            productAdded,
            productAddedID,
            searchResultsLength,
            searchQuery,
            setSearchQuery,
            setAutocompleteOpen,
            setCartOpen,
            setFiltersOpen,
            setSearchResultsLength,
            setProductAdded,
            setProductAddedID,
            setTheme,
            theme,
            alerts,
            setAlerts,
            addCounter,
            setAddCounter,
            seeAllCarouselId,
            setSeeAllCarouselId,
            scrollToSeeAllProductId,
            setScrollToSeeAllProductId,
            appliedFilters,
            setAppliedFilters,
            searchOpen,
            setSearchOpen,
            currentAisleId,
            setCurrentAisleId,
            openWindowOnboardingModalOpen,
            setOpenWindowOnboardingModalOpen,
            closedWindowOnboardingModalOpen,
            setClosedWindowOnboardingModalOpen,
            productsModal,
            setProductsModal,
            productsModalDataRef,
            recurringModal,
            setRecurringModal,
            soldOutRecurringModal,
            setSoldOutRecurringModal,
            recurringToast,
            setRecurringToast,
            recurringLimitWarningModal,
            setRecurringLimitWarningModal,
            ebtPaymentFlowOpen,
            setEbtPaymentFlowOpen,
            gwpErrorToast,
            setGwpErrorToast,
            lastPath,
        }),
        [
            addCounter,
            alerts,
            autocompleteOpen,
            cartOpen,
            closedWindowOnboardingModalOpen,
            currentAisleId,
            filtersOpen,
            gwpErrorToast,
            openWindowOnboardingModalOpen,
            productAdded,
            searchQuery,
            searchResultsLength,
            theme,
            seeAllCarouselId,
            scrollToSeeAllProductId,
            appliedFilters,
            searchOpen,
            ebtPaymentFlowOpen,
            productsModal,
            recurringModal,
            soldOutRecurringModal,
            recurringToast,
            recurringLimitWarningModal,
        ]
    );

    return <GlobalContext.Provider value={contextValue}>{children}</GlobalContext.Provider>;
}

export { GlobalContext, GlobalProvider };
